import { useContext, useEffect, useMemo, useState } from 'react';
import {
    ThemeProvider,
    useMediaQuery,
    Menu,
    IconButton,
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MoreVert, Edit, Delete } from '@mui/icons-material';
import { Context } from '../../../context/UserContext';
import { useMRTState } from '../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../helpers/mrt/getMRTDefaultProps';
import resolveResponsiveToolbarActions from '../../../helpers/mrt/resolveResponsiveToolbarActions';
import tableThemeCustom from '../../form/tableThemeCustom';
import CRUDs from '../../../api/CRUDs';
import { campos, grupos, disponivel, verdadeiro, mensagemErro } from './campos';
const api = CRUDs.perfisPermissao;
const chave = (sub) => String(sub.id);

export default function Index() {
    const { parametro } = useContext(Context);
    const theme = tableThemeCustom();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const mrt = useMRTState({ storageKey: 'perfisPermissao.lista', pageSizeDefault: parametro?.paginacao || 10 });
    const [menuAcoes, setMenuAcoes] = useState(null);
    const [erroConsulta, setErroConsulta] = useState('');
    const columns = useMemo(() => [{ accessorKey: 'descricao', header: 'Perfil', size: 320 }], []);
    const [catalogo, setCatalogo] = useState({ empresas: [], menus: [], submenus: [] });
    const [empresaId, setEmpresaId] = useState('');
    const [perfis, setPerfis] = useState([]);
    const [erro, setErro] = useState('');
    const [sucesso, setSucesso] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [reload, setReload] = useState(0);
    const [editor, setEditor] = useState(null);
    const [grupo, setGrupo] = useState('Padrão');
    const [busca, setBusca] = useState('');
    useEffect(() => {
        let ativo = true;
        api.catalogo()
            .then(({ data }) => {
                if (!ativo) return;
                setCatalogo(data.data);
                const atual = localStorage.getItem('empresaId');
                setEmpresaId(
                    String(data.data.empresas.find((e) => String(e.id) === atual)?.id || data.data.empresas[0]?.id || '')
                );
            })
            .catch((e) => {
                if (ativo) { setErro(mensagemErro(e)); setErroConsulta(mensagemErro(e)); }
            })
            .finally(() => {
                if (ativo) setLoading(false);
            });
        return () => {
            ativo = false;
        };
    }, []);
    useEffect(() => {
        if (!empresaId) return;
        let ativo = true;
        setLoading(true);
        setPerfis([]);
        setErro('');
        setErroConsulta('');
        api.listar(empresaId)
            .then(({ data }) => {
                if (ativo) setPerfis(data.data);
            })
            .catch((e) => {
                if (ativo) { setErro(mensagemErro(e)); setErroConsulta(mensagemErro(e)); }
            })
            .finally(() => {
                if (ativo) setLoading(false);
            });
        return () => {
            ativo = false;
        };
    }, [empresaId, reload]);
    const nomesMenu = useMemo(() => Object.fromEntries(catalogo.menus.map((m) => [m.id, m.descricao])), [catalogo.menus]);
    const submenus = catalogo.submenus
        .filter((s) => nomesMenu[s.menuId])
        .sort((a, b) => {
            const comparar = (x, y) =>
                String(x ?? '').localeCompare(String(y ?? ''), 'pt-BR', {
                    sensitivity: 'base',
                    numeric: true,
                });

            return comparar(nomesMenu[a.menuId], nomesMenu[b.menuId]) || comparar(a.descricao, b.descricao);
        });
    const visiveis = submenus.filter((s) => `${nomesMenu[s.menuId]} ${s.descricao}`.toLowerCase().includes(busca.toLowerCase()));
    const indisponiveis = editor ? Object.keys(editor.itens).filter((k) => !submenus.some((s) => chave(s) === k)) : [];
    async function abrir(perfil) {
        setErro('');
        setSucesso('');
        setBusca('');
        setGrupo('Padrão');
        if (!perfil) {
            setEditor({ descricao: '', itens: {}, vinculados: 0 });
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.consultar(empresaId, perfil.id);
            const p = data.data;
            setEditor({
                id: p.id,
                descricao: p.descricao,
                vinculados: p.vinculos.length,
                itens: Object.fromEntries(p.itens.map((i) => [String(i.menuSubId), i])),
            });
        } catch (e) {
            setErro(mensagemErro(e));
        } finally {
            setBusy(false);
        }
    }
    function selecionar(sub, marcado) {
        setEditor((prev) => {
            const itens = { ...prev.itens };
            if (marcado) itens[chave(sub)] = { menuId: sub.menuId, menuSubId: sub.id, visualizar: true };
            else delete itens[chave(sub)];
            return { ...prev, itens };
        });
    }
    function alterar(sub, campo, valor) {
        setEditor((prev) => ({ ...prev, itens: { ...prev.itens, [chave(sub)]: { ...prev.itens[chave(sub)], [campo]: valor } } }));
    }
    async function salvar() {
        const itens = submenus
            .filter((s) => editor.itens[chave(s)])
            .map((s) => ({
                menuId: s.menuId,
                menuSubId: s.id,
                ...Object.fromEntries(campos.map(([c]) => [c, disponivel(s, c) && verdadeiro(editor.itens[chave(s)][c])])),
            }));
        if (
            !window.confirm(
                `Salvar este perfil e atualizar ${editor.vinculados} usuário(s) vinculado(s)?${!itens.length ? '\nO perfil ficará vazio e removerá os acessos dos vinculados.' : ''}`
            )
        )
            return;
        setBusy(true);
        setErro('');
        try {
            const { data } = await api.salvar({
                empresaId: Number(empresaId),
                perfilPermissaoId: editor.id,
                descricao: editor.descricao,
                itens,
                confirmarPerfilVazio: !itens.length,
            });
            setSucesso(data.message);
            setEditor(null);
            setReload((r) => r + 1);
        } catch (e) {
            setErro(mensagemErro(e));
        } finally {
            setBusy(false);
        }
    }
    async function excluir(p) {
        if (!window.confirm(`Excluir o perfil ${p.descricao}? Perfis com usuários vinculados não podem ser excluídos.`)) return;
        setBusy(true);
        setErro('');
        try {
            const { data } = await api.deletar({ empresaId: Number(empresaId), perfilPermissaoId: p.id });
            setSucesso(data.message);
            setReload((r) => r + 1);
        } catch (e) {
            setErro(mensagemErro(e));
        } finally {
            setBusy(false);
        }
    }
    const mrtDefault = getMRTDefaultProps({
        mrt: { ...mrt, isLoading: loading, isRefetching: busy, isError: !!erroConsulta },
        parametro,
        isMobile,
    });
    const toolbar = resolveResponsiveToolbarActions([
        { key: 'novo', label: 'Novo perfil', variant: 'contained', mobileVisible: true,
          disabled: !empresaId || loading || busy || !!erroConsulta, onClick: () => abrir(null) },
        { key: 'atualizar', label: 'Atualizar', disabled: !empresaId || loading || busy,
          onClick: () => setReload(r => r + 1) },
    ], { isMobile });
    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>
                Perfis de acesso
            </Typography>
            <Stack spacing={2}>
                {erro && !editor && <Alert severity="error">{erro}</Alert>}
                {sucesso && <Alert severity="success">{sucesso}</Alert>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        select
                        label="Empresa"
                        value={empresaId}
                        onChange={(e) => {
                            mrt.setPagination(p => ({ ...p, pageIndex: 0 }));
                            setEmpresaId(e.target.value);
                        }}
                        disabled={busy || loading}
                        sx={{ minWidth: 260 }}
                    >
                        {catalogo.empresas.map((e) => (
                            <MenuItem key={e.id} value={String(e.id)}>
                                {e.nomeFantasia || `Empresa ${e.id}`}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
                <ThemeProvider theme={theme}>
                    <MaterialReactTable
                        {...mrtDefault}
                        columns={columns}
                        data={perfis}
                        getRowId={row => String(row.id)}
                        manualFiltering={false}
                        manualPagination={false}
                        manualSorting={false}
                        rowCount={undefined}
                        autoResetPageIndex={true}
                        enableRowSelection={false}
                        renderRowActions={({ row }) => (
                            <Stack direction={isMobile ? 'column' : 'row'} spacing={0.5}>
                                <IconButton aria-label={`Editar ${row.original.descricao}`} title="Editar" disabled={busy || loading} onClick={() => abrir(row.original)}>
                                    <Edit />
                                </IconButton>
                                <IconButton aria-label={`Excluir ${row.original.descricao}`} title="Excluir" color="error" disabled={busy || loading} onClick={() => excluir(row.original)}>
                                    <Delete />
                                </IconButton>
                            </Stack>
                        )}
                        renderTopToolbarCustomActions={() => (
                            <Stack direction="row" spacing={1} alignItems="center">
                                {toolbar.visibleActions.map(action => (
                                    <Button key={action.key} variant={action.variant || 'text'} disabled={action.disabled} onClick={action.onClick}>
                                        {action.label}
                                    </Button>
                                ))}
                                {!!toolbar.menuActions.length && <>
                                    <IconButton aria-label="Mais ações" onClick={e => setMenuAcoes(e.currentTarget)}><MoreVert /></IconButton>
                                    <Menu anchorEl={menuAcoes} open={!!menuAcoes} onClose={() => setMenuAcoes(null)}>
                                        {toolbar.menuActions.map(action => (
                                            <MenuItem key={action.key} disabled={action.disabled} onClick={() => { setMenuAcoes(null); action.onClick(); }}>
                                                {action.label}
                                            </MenuItem>
                                        ))}
                                    </Menu>
                                </>}
                            </Stack>
                        )}
                    />
                </ThemeProvider>
            </Stack>
            <Dialog
                open={!!editor}
                onClose={() => {
                    if (!busy) setEditor(null);
                }}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>{editor?.id ? 'Editar perfil' : 'Novo perfil'}</DialogTitle>
                <DialogContent>
                    {erro && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {erro}
                        </Alert>
                    )}
                    {!!indisponiveis.length && (
                        <Alert
                            severity="warning"
                            action={
                                <Button
                                    disabled={busy}
                                    onClick={() => {
                                        if (!window.confirm('Remover do perfil os submenus internos ou indisponíveis?')) return;
                                        setEditor((p) => ({
                                            ...p,
                                            itens: Object.fromEntries(
                                                Object.entries(p.itens).filter(([k]) => !indisponiveis.includes(k))
                                            ),
                                        }));
                                    }}
                                >
                                    Remover
                                </Button>
                            }
                        >
                            {indisponiveis.length} submenu(s) do perfil estão internos ou indisponíveis. Remova-os para salvar.
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        fullWidth
                        label="Descrição do perfil"
                        margin="normal"
                        value={editor?.descricao || ''}
                        disabled={busy}
                        inputProps={{ maxLength: 100 }}
                        onChange={(e) => setEditor((p) => ({ ...p, descricao: e.target.value }))}
                    />
                    <Typography variant="body2">
                        Selecione as rotinas e marque os acessos. Salvar atualiza todos os usuários vinculados.
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        margin="normal"
                        label="Buscar menu ou submenu"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                    />
                    <Tabs value={grupo} onChange={(_, v) => setGrupo(v)} variant="scrollable" scrollButtons="auto">
                        {Object.keys(grupos).map((g) => (
                            <Tab key={g} value={g} label={g} />
                        ))}
                    </Tabs>
                    <TableContainer sx={{ maxHeight: 440 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Usar</TableCell>
                                    <TableCell>Menu / Submenu</TableCell>
                                    {grupos[grupo].map(([c, l]) => (
                                        <TableCell key={c} align="center">
                                            {l}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visiveis.map((s) => (
                                    <TableRow key={s.id}>
                                        <TableCell>
                                            <Checkbox
                                                checked={!!editor?.itens[chave(s)]}
                                                disabled={busy}
                                                onChange={(_, v) => selecionar(s, v)}
                                                inputProps={{ 'aria-label': `Usar ${s.descricao}` }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">{nomesMenu[s.menuId]}</Typography>
                                            <Typography variant="body2">{s.descricao}</Typography>
                                        </TableCell>
                                        {grupos[grupo].map(([c, l]) => (
                                            <TableCell key={c} align="center">
                                                <Checkbox
                                                    checked={disponivel(s, c) && verdadeiro(editor?.itens[chave(s)]?.[c])}
                                                    disabled={busy || !editor?.itens[chave(s)] || !disponivel(s, c)}
                                                    onChange={(_, v) => alterar(s, c, v)}
                                                    inputProps={{ 'aria-label': `${l}: ${s.descricao}` }}
                                                />
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setEditor(null)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={busy || !editor?.descricao.trim() || !!indisponiveis.length}
                        onClick={salvar}
                    >
                        {busy ? 'Salvando…' : 'Salvar perfil'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
